import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {addHighlightedIdToDisplayProperty, getRandomDisplacement} from '../../../components/slides/form/slide.form.utils';
import i18n from '../../../i18n';
import {DeviceType} from '../../../incase-domain/common/DeviceType.enum';
import {IElement} from '../../../incase-domain/elements';
import {ELEMENT} from '../../../incase-domain/elements/base/ElementTypes';
import {ISlide} from '../../../incase-domain/slide';
import {ElementProps, getEmptyElement} from '../../../service/element';
import {setClipboardDataAction} from '../../../store/actions/clipboard.actions';
import {setSidePanelContentAction} from '../../../store/actions/grid';
import {bringElementToFrontAction, sendElementToBackAction, setHighlightedElementIndex} from '../../../store/actions/slide';
import {SidePanelContent} from '../../../store/actions/types/grid.types';
import {clipboardDataSelector} from '../../../store/selectors/clipboard.selectors';
import {selectedPresentationSelector, selectedViewSelector} from '../../../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {useRules} from '../../../ui/hooks/rulesHook';
import {getElementDefaultName} from '../../element';
import {exportElement} from '../../exportElement';
import {removeArrayElement, swapArrayElement} from '../../utils';
import {useSlideUpdateContext} from '../../../ui/context/SetSlideUpdateContext';

type ConnectedQuestionReports = {
    slide: ISlide;
    element: IElement;
};

const useElementActions = (): {
    addNewElement: <T extends ELEMENT>(type: T, newElementProps?: ElementProps<T>) => void;
    duplicateElement: (elementIndex: number, openHighlighted?: boolean) => void;
    copyElement: (elementIndex: number) => Promise<void>;
    cutElement: (elementIndex: number) => Promise<void>;
    pasteElement: () => Promise<void>;
    updateElement: (elementIndex: number, element: IElement) => void;
    updateElementFitContent: (elementIndex: number, element: IElement, fitContent: boolean, fitContentStopValue: boolean) => void;
    bringToFront: (elementIndex: number) => void;
    sendToBack: (elementIndex: number) => void;
    deleteElement: (elementIndex: number) => void;
    importElement: (element: IElement) => void;
    exportElement: (elementIndex: number) => Promise<void>;
} => {
    const dispatch = useDispatch();

    const slide = useSelector(selectedSlideSelector);
    const presentation = useSelector(selectedPresentationSelector);
    const selectedView = useSelector(selectedViewSelector);
    const clipboardData = useSelector(clipboardDataSelector);
    const {setSlide} = useSlideUpdateContext();

    const {checkIfElementIsUsedInAppliedRules} = useRules();

    function getElementIndexFromMobileOrder(mobileOrder: number): number {
        return slide.elements.findIndex((element) => element.mobileOrder === mobileOrder);
    }

    const duplicateElement = (elementIndex: number, openHighlighted = true): void => {
        let index = elementIndex;
        if (selectedView === DeviceType.MOBILE) {
            index = getElementIndexFromMobileOrder(elementIndex);
        }
        if (index < 0) return;

        const elementToCopy = slide.elements[index];
        const {xPosition, yPosition, height, width} = elementToCopy;
        const newElementCopy = {
            ...elementToCopy,
            xPosition: getRandomDisplacement(xPosition, width),
            yPosition: getRandomDisplacement(yPosition, height),
            id: `${slide._id}-${uuid()}`,
        };
        const newElements = [...addHighlightedIdToDisplayProperty(slide.elements, newElementCopy), newElementCopy];

        setSlide({...slide, elements: newElements});
        dispatch(setHighlightedElementIndex(newElements.length - 1));

        if (openHighlighted) {
            dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
        }
    };

    const addNewElement = <T extends ELEMENT>(type: T, newElementProps?: ElementProps<T>): void => {
        const emptyElement = getEmptyElement(slide._id, type, newElementProps);
        const numberOfSameElementsOnSlide = slide.elements.filter((element) => element.type === type).length;
        const newElement = {...emptyElement, name: `${getElementDefaultName(type)} ${numberOfSameElementsOnSlide + 1}`};
        const updatedSlide = {
            ...slide,
            elements: [...addHighlightedIdToDisplayProperty(slide.elements || [], newElement), newElement],
        };
        setSlide(updatedSlide);
        dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
        dispatch(setHighlightedElementIndex(slide.elements.length));
    };

    const updateElement = useCallback(
        (elementIndex: number, element: IElement) => {
            const newElements = swapArrayElement(slide.elements, elementIndex, element);
            setSlide({...slide, elements: newElements});
        },
        [setSlide, slide]
    );

    const updateElementFitContent = useCallback(
        (elementIndex: number, element: IElement, fitContent: boolean, fitContentStopValue: boolean): void => {
            if (element.fitContent === fitContentStopValue) {
                updateElement(elementIndex, {...element, fitContent});
            }
        },
        [updateElement]
    );

    const bringToFront = (elementIndex: number): void => {
        dispatch(bringElementToFrontAction(elementIndex));
    };

    const sendToBack = (elementIndex: number): void => {
        dispatch(sendElementToBackAction(elementIndex));
    };

    const findConnectedQuestionReports = (index: number): ConnectedQuestionReports[] => {
        const selectedElement = slide.elements[index];
        const arrOfConnectedQuestionReports: ConnectedQuestionReports[] = [];

        const allSlides = presentation.slides.map((s) => (s._id === slide._id ? slide : s));

        allSlides.forEach((currentSlide) => {
            const slideElementsForReport = currentSlide.elements
                .filter((element) => element.type === ELEMENT.QUESTION_REPORT && selectedElement.id === element.questionElementId)
                .map((element) => {
                    return {slide: currentSlide, element};
                });

            arrOfConnectedQuestionReports.push(...slideElementsForReport);
        });

        return arrOfConnectedQuestionReports;
    };

    const deleteElement = (elementIndex: number): void => {
        let index = elementIndex;
        if (selectedView === DeviceType.MOBILE) {
            index = getElementIndexFromMobileOrder(elementIndex);
        }
        if (index < 0) return;

        const element = slide.elements[index];
        if (checkIfElementIsUsedInAppliedRules(element.id)) {
            toast.error(i18n.t('toasts.deleteElementWithAppliedRuleError'));
        } else {
            const connectedQuestionReports = findConnectedQuestionReports(index);

            if (connectedQuestionReports.length) {
                const reports = connectedQuestionReports.map(({slide, element}) => `${element.name} - (${slide.name})`).join(', ');
                toast.error(`${i18n.t('editorPage.model.deleteElementWarning')} ${reports}`);
            } else {
                const elementsWithUpdatedDisplayProperty = addHighlightedIdToDisplayProperty(slide.elements, {
                    ...slide.elements[index],
                    elementToDisplayAfterClick: [],
                    elementToClickForDisplay: [],
                });

                const elements = removeArrayElement(elementsWithUpdatedDisplayProperty, index);
                setSlide({...slide, elements});
                dispatch(setSidePanelContentAction(SidePanelContent.SLIDE));
            }
        }
    };

    const copyElement = async (elementIndex: number): Promise<void> => {
        let index = elementIndex;
        if (selectedView === DeviceType.MOBILE) {
            index = getElementIndexFromMobileOrder(elementIndex);
        }
        if (index < 0) return;

        const elementToCopy = slide.elements[index];
        const copiedElement = {
            ...elementToCopy,
            id: `${slide._id}-${uuid()}`,
        };

        dispatch(setClipboardDataAction(copiedElement));
    };

    const pasteElement = async (): Promise<void> => {
        if (!clipboardData) return;

        const pastedElement = {
            ...clipboardData,
            id: `${slide._id}-${uuid()}`,
            slideId: slide._id,
        };

        const newElements = [...addHighlightedIdToDisplayProperty(slide.elements, pastedElement), pastedElement];
        setSlide({...slide, elements: newElements});
        dispatch(setHighlightedElementIndex(newElements.length - 1));
        dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
    };

    const cutElement = async (elementIndex: number): Promise<void> => {
        let index = elementIndex;
        if (selectedView === DeviceType.MOBILE) {
            index = getElementIndexFromMobileOrder(elementIndex);
        }
        if (index < 0) return;

        const elementToCut = slide.elements[index];

        if (checkIfElementIsUsedInAppliedRules(elementToCut.id)) {
            toast.error(i18n.t('toasts.deleteElementWithAppliedRuleError'));
        } else {
            const connectedQuestionReports = findConnectedQuestionReports(index);
            if (connectedQuestionReports.length) {
                const reports = connectedQuestionReports.map(({slide, element}) => `${element.name} - (${slide.name})`).join(', ');
                toast.error(`${i18n.t('editorPage.model.deleteElementWarning')} ${reports}`);
                return;
            }

            const cutElement = {
                ...elementToCut,
                id: `${slide._id}-${uuid()}`,
            };

            dispatch(setClipboardDataAction(cutElement));

            const elementsWithUpdatedDisplayProperty = addHighlightedIdToDisplayProperty(slide.elements, {
                ...elementToCut,
                elementToDisplayAfterClick: [],
                elementToClickForDisplay: [],
            });

            const elements = removeArrayElement(elementsWithUpdatedDisplayProperty, index);
            setSlide({...slide, elements});
            dispatch(setSidePanelContentAction(SidePanelContent.SLIDE));
        }
    };

    const handleExportElement = async (elementIndex: number): Promise<void> => {
        await exportElement(elementIndex, slide.elements);
    };

    const importElement = (element: IElement): void => {
        addNewElement(element.type, {...element});
    };

    return {
        addNewElement,
        duplicateElement,
        copyElement,
        cutElement,
        pasteElement,
        updateElement,
        updateElementFitContent,
        bringToFront,
        sendToBack,
        deleteElement,
        importElement,
        exportElement: handleExportElement,
    };
};

export default useElementActions;
